import {
  playbookBriefOptions,
  playbookDetailOptions,
  workspaceDetailedOptions,
} from '@doneverse/data/queries'
import type { Json, Playbook, PlaybookBrief, WorkspacePlaybookStatus } from '@doneverse/shared'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './CoachGraceWidget.module.css'
import coachGraceAvatarImg from '@/assets/brand/coach-grace-avatar.png'
import { useAuth } from '@/contexts/AuthContext'
import { COACH_GRACE_OPEN_EVENT_NAME } from '@/contexts/CoachGraceContext'
import { useElevenLabsWidget } from '@/hooks/useElevenLabsWidget'
import { useOnboardingLock } from '@/hooks/useOnboardingLock'

const TRANSITION = { duration: 0.2, ease: [0.22, 1, 0.36, 1] as const }

function buildStatusFirstMessage(playbook: Playbook, status: WorkspacePlaybookStatus): string {
  if (status === 'completed') {
    return `Nice — you've already completed the "${playbook.name}" playbook! Want to revisit anything or talk about next steps?`
  }
  return `I see you've already briefed the "${playbook.name}" playbook. What would you like to work on?`
}

function isMeaningfulBriefValue(value: Json | undefined): boolean {
  if (Array.isArray(value)) return value.length > 0
  return typeof value === 'string' ? value.trim().length > 0 : value != null
}

function getAnsweredQuestionIds(brief: Json | undefined): string[] {
  if (!brief || typeof brief !== 'object' || Array.isArray(brief)) return []
  return Object.entries(brief)
    .filter(([, value]) => isMeaningfulBriefValue(value))
    .map(([questionId]) => questionId)
}

function hasMeaningfulBriefAnswer(brief: Json | undefined): boolean {
  if (!brief || typeof brief !== 'object' || Array.isArray(brief)) return false
  return Object.values(brief).some((value) => isMeaningfulBriefValue(value))
}

function buildBriefFirstMessage(playbook: Playbook, existingBrief?: PlaybookBrief): string {
  if (existingBrief?.status === 'draft' && hasMeaningfulBriefAnswer(existingBrief.brief)) {
    return `Looks like you already started the "${playbook.name}" playbook. I'll pick up from your saved answers.`
  }

  const firstQuestion = playbook.schema
    .flatMap((section) => section.questions)
    .find((question) => question.question?.trim())
  const questionText = firstQuestion?.question ?? "Let's get started."
  return `Let's brief the "${playbook.name}" playbook together! I'll walk you through each question. Here's the first one: ${questionText}`
}

function extractPlaybookSlug(pathname: string): string | null {
  const match = pathname.match(/^\/playbooks\/([^/]+)$/)
  return match ? match[1] : null
}

export function CoachGraceWidget() {
  const { user, profile } = useAuth()
  const location = useLocation()
  const containerRef = useRef<HTMLDivElement>(null)

  const [minimized, setMinimized] = useState(false)
  const [nativeCallInterrupt, setNativeCallInterrupt] = useState(0)

  const isMessagesPage = location.pathname.startsWith('/messages')
  const isFounder = profile?.role === 'founder'
  // The onboarding lock reaches this widget too. It is fixed to the viewport and
  // mounted in the root layout, so it survives on the one surface the lock lets
  // a founder onto — the active step's own page, where the tracker overlay
  // deliberately yields. Without this, "the dashboard is not usable yet" would
  // still hand a pre-brief founder the AI coach.
  const { locked } = useOnboardingLock()
  const shouldMount = !!user && !!profile && isFounder && !locked
  const shouldShowWidget = shouldMount && !isMessagesPage && !minimized
  const shouldShowAvatar = shouldMount && !isMessagesPage && minimized

  const playbookSlug = extractPlaybookSlug(location.pathname)
  // Gated on `isFounder` as well as the slug, not just the slug: this component
  // is mounted in the root layout for every role, and `GET /workspace` answers
  // anyone else 403. Nothing rendered it, but the failure landed in the shared
  // cache entry, where the playbook page — which reads the same key — took it
  // for "this playbook does not exist".
  const { data: playbook } = useQuery({
    ...playbookDetailOptions(playbookSlug ?? ''),
    enabled: isFounder && !!playbookSlug,
  })
  const { data: workspace } = useQuery({
    ...workspaceDetailedOptions(),
    enabled: isFounder && !!playbookSlug,
  })

  const workspacePlaybook = workspace?.playbooks?.find(
    (item) => item.playbook?.slug === playbookSlug || item.playbook_id === playbook?.id,
  )
  const isToBeBriefed = workspacePlaybook?.status === 'to_be_briefed'
  const playbookBriefId = workspacePlaybook?.playbook_brief_id ?? null
  const { data: playbookBrief } = useQuery(playbookBriefOptions(playbookBriefId))

  const widgetOptions = useMemo(
    () =>
      playbook
        ? {
            playbookId: playbook.id,
            playbookName: playbook.name,
            playbookType: playbook.type,
            playbookBriefId: playbookBriefId ?? undefined,
            playbookBriefStatus: playbookBrief?.status ?? undefined,
            workspacePlaybookStatus: workspacePlaybook?.status ?? undefined,
            playbookBriefAnswers: JSON.stringify(playbookBrief?.brief ?? {}),
            playbookAnsweredQuestionIds: JSON.stringify(getAnsweredQuestionIds(playbookBrief?.brief)),

            ...(isToBeBriefed
              ? {
                  playbookQuestions: JSON.stringify(playbook.schema ?? []),
                  overrideFirstMessage: buildBriefFirstMessage(playbook, playbookBrief),
                }
              : {
                  overrideFirstMessage: buildStatusFirstMessage(
                    playbook,
                    workspacePlaybook?.status ?? 'briefed',
                  ),
                }),
          }
        : undefined,
    [playbook, isToBeBriefed, playbookBrief, playbookBriefId, workspacePlaybook],
  )

  // Always keep widget mounted for founders so it doesn't lose state. The reset
  // key deliberately excludes the route: page context reaches the widget through
  // its observed `dynamic-variables` / `override-first-message` attributes, which
  // the hook keeps in sync, so navigating no longer has to rebuild the element
  // (which would drop an in-progress conversation). A native call is the only
  // thing that still forces a rebuild.
  useElevenLabsWidget(containerRef, shouldMount, `native-call-${nativeCallInterrupt}`, widgetOptions)

  const handleDismiss = () => setMinimized(true)
  const handleRestore = () => setMinimized(false)

  useEffect(() => {
    const handleOpen = () => setMinimized(false)
    document.addEventListener(COACH_GRACE_OPEN_EVENT_NAME, handleOpen)
    return () => document.removeEventListener(COACH_GRACE_OPEN_EVENT_NAME, handleOpen)
  }, [])

  // Recreating the embedded widget is its supported teardown path; doing it
  // before the native call starts releases Coach Grace's WebView microphone.
  useEffect(() => {
    const interrupt = () => setNativeCallInterrupt((value) => value + 1)
    document.addEventListener('doneverse:native-call-starting', interrupt)
    return () => document.removeEventListener('doneverse:native-call-starting', interrupt)
  }, [])

  if (!shouldMount) {
    return null
  }

  return (
    <>
      <div
        data-testid="coach-grace-widget-root"
        className={styles.widgetWrapper}
        style={{
          display: isMessagesPage ? 'none' : undefined,
          opacity: shouldShowWidget ? 1 : 0,
          // `pointer-events: none` on the wrapper doesn't cascade into the embedded
          // ElevenLabs element (its own children set `pointer-events: auto`), so a
          // minimized widget would keep intercepting taps over page content. Hiding
          // it removes the whole subtree from hit-testing while staying mounted so
          // the widget keeps its conversation state.
          visibility: shouldShowWidget ? undefined : 'hidden',
          pointerEvents: shouldShowWidget ? 'auto' : 'none',
        }}
      >
        <button
          type="button"
          className={styles.dismissButton}
          onClick={handleDismiss}
          title="Minimize Coach Grace AI"
          aria-label="Minimize Coach Grace AI"
        >
          <X size={12} />
        </button>
        <div ref={containerRef} />
      </div>
      <AnimatePresence>
        {shouldShowAvatar && (
          <motion.button
            type="button"
            data-testid="coach-grace-widget-avatar"
            className={styles.avatarButton}
            onClick={handleRestore}
            aria-label="Open Coach Grace AI"
            title="Open Coach Grace AI"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={TRANSITION}
          >
            <img src={coachGraceAvatarImg} alt="" className={styles.avatarImage} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  )
}
